/**
 * En qué plan está quien mira el panel.
 *
 * Tres sitios lo preguntan —la insignia del menú de usuario, la de «Planes» y la
 * tarjeta de mejorar plan— y los tres lo hacían igual: pedir la sesión, llamar a
 * `refreshBilling`, guardar el resultado. Aquí sólo hay una copia de eso.
 *
 * ── Por qué `confirmed` ─────────────────────────────────────────────────────
 * Porque el valor de arranque es una CACHÉ, y por defecto vale «free». Quien
 * paga y abre el panel en un navegador nuevo empieza, durante una ida y vuelta,
 * contado como del plan gratis — y cualquier cosa que se enseñe con esa premisa
 * es un cartel de «mejora tu plan» delante de alguien que ya pagó.
 *
 * `plan` sirve para pintar de inmediato lo que da igual equivocarse un instante
 * (una insignia que dirá Pro un pelo después). `confirmed` es para lo que NO se
 * puede enseñar de más: entonces se espera a que el servidor conteste.
 */
import { useEffect, useState } from 'react'
import { supabase } from './supabase'
import { cachedBillingPlan, refreshBilling, type BillingPlan } from './api'

export function useBillingPlan(): { plan: BillingPlan; confirmed: boolean } {
  const [plan, setPlan] = useState<BillingPlan>(cachedBillingPlan)
  const [confirmed, setConfirmed] = useState(false)

  useEffect(() => {
    let alive = true
    void supabase.auth.getSession().then(({ data }) => {
      const token = data.session?.access_token
      /* Sin sesión no hay nada que confirmar, y quien no ha entrado no ve el
         panel: se queda con la caché y sin confirmar. */
      if (!token) return
      refreshBilling(token)
        .then((billing) => {
          if (!alive) return
          setPlan(billing.plan)
          setConfirmed(true)
        })
        .catch(() => {
          /* Un fallo de red no confirma nada. Lo que dependa de `confirmed`
             seguirá callado, que es el lado correcto en el que equivocarse. */
        })
    })
    return () => { alive = false }
  }, [])

  return { plan, confirmed }
}
